import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';

import PbxContext from '../../../context/PbxContext';

import { accessLocalStorage, updateIntegration } from '../../../services';

import Loading from '../../../components/Loading/LoadingModule';

import '../../../libs/bulma.min.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

function IntegrationsEdit() {
  const getItensStateGlobal = useContext(PbxContext);
  const { itemsSelectedToEdit, toggleIsHidden, toggleIsChangeFormElements } = getItensStateGlobal;

  let url = window.location.href;
  url = url.split('/')[2];

  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({
    accountcode: '',
    type: '',
    url: '',
    token: '',
    message: '',
    template_name: '',
    template_language: 'pt_BR'
  });
  const [validate, setValidate] = useState({});
  const [disable, setDisable] = useState(true);
  const [saving, setSaving] = useState(false);

  // Funções locais para controlar visibilidade e bordas dos elementos
  const changeVisibilityMessage = (show, elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      if (show) {
        element.classList.remove('is-hidden');
      } else {
        element.classList.add('is-hidden');
      }
    }
  };

  const changeBorderToRedOrNormal = (isValid, fieldName) => {
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      if (isValid) {
        element.classList.remove('is-danger');
      } else {
        element.classList.add('is-danger');
      }
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = await accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    if (dataUser && dataUser.user?.role !== 'root') return history.push('/home');
    
    // Verificar se há dados para editar
    if (!itemsSelectedToEdit || !itemsSelectedToEdit.id_integration) {
      return history.push('/config/integracoes/lista');
    }

    setValues({
      accountcode: itemsSelectedToEdit.accountcode || '',
      type: itemsSelectedToEdit.type || '',
      url: itemsSelectedToEdit.url || '',
      token: itemsSelectedToEdit.token || '',
      message: itemsSelectedToEdit.message || '',
      template_name: itemsSelectedToEdit.template_name || '',
      template_language: itemsSelectedToEdit.template_language || 'pt_BR'
    });

    setLoading(false);
  }, [history, itemsSelectedToEdit]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  // Verificar estado de validação sempre que values ou validate mudarem
  useEffect(() => {
    const requiredFields = ['accountcode', 'type', 'url', 'token'];
    
    // Verificar se todos os campos obrigatórios estão preenchidos
    const allFieldsFilled = requiredFields.every(field => 
      values[field] && values[field].trim() !== ''
    );
    
    // Verificar se todos os campos são válidos (incluindo message se preenchida)
    const allFieldsValid = requiredFields.every(field => {
      if (validate[field]) {
        return validate[field].isValid;
      }
      // Se o campo não foi validado ainda mas está preenchido, validar agora
      if (values[field] && values[field].trim() !== '') {
        const fieldValidation = validateField(field, values[field]);
        return fieldValidation.isValid;
      }
      return false;
    });

    let messageValid = true;
    if (values.message && values.message.trim() !== '') {
      const messageValidation = validate.message || validateField('message', values.message);
      messageValid = messageValidation.isValid;
    }

    let templateValid = true;
    if (values.template_name && values.template_name.trim() !== '') {
      const templateValidation = validate.template_name || validateField('template_name', values.template_name);
      templateValid = templateValidation.isValid;
    }

    setDisable(!(allFieldsFilled && allFieldsValid && messageValid && templateValid));
  }, [values, validate]);

  const changeScreen = (check, name) => {
    changeBorderToRedOrNormal(check, name);
    changeVisibilityMessage(!check, `${name}-invalid-data`);
  };

  const validateField = (name, value) => {
    let isValid = true;
    let errorMessage = '';

    switch (name) {
      case 'accountcode':
        isValid = value.trim().length >= 1 && value.trim().length <= 3;
        errorMessage = 'Account code deve ter entre 1 e 3 caracteres';
        break;
      case 'type':
        isValid = value.trim().length >= 1 && value.trim().length <= 4;
        errorMessage = 'DDD deve ter entre 1 e 4 caracteres';
        break;
      case 'url':
        if (value.trim().length === 0) {
          isValid = false;
          errorMessage = 'URL é obrigatória';
        } else {
          try {
            new URL(value);
            isValid = true;
          } catch {
            isValid = false;
            errorMessage = 'URL deve ser válida (ex: https://api.exemplo.com)';
          }
        }
        break;
      case 'token':
        isValid = value.trim().length >= 1;
        errorMessage = 'Token é obrigatório';
        break;
      case 'message':
        if (value.trim().length === 0) {
          isValid = true;
        } else if (value.length > 280) {
          isValid = false;
          errorMessage = 'Message deve ter no máximo 280 caracteres';
        } else {
          const suspiciousPatterns = [
            /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi,
            /(--|\/\*|\*\/)/g,
            /(<script|javascript:|vbscript:|onload|onerror)/gi
          ];
          const hasSuspiciousContent = suspiciousPatterns.some(pattern => pattern.test(value));
          isValid = !hasSuspiciousContent;
          if (!isValid) errorMessage = 'Message contém caracteres ou padrões não permitidos';
        }
        break;
      case 'template_name':
        if (value.trim().length === 0) {
          isValid = true;
        } else if (value.length > 100) {
          isValid = false;
          errorMessage = 'Template name deve ter no máximo 100 caracteres';
        } else if (!/^[a-z0-9_]+$/.test(value)) {
          isValid = false;
          errorMessage = 'Template name deve conter apenas letras minúsculas, números e underline';
        } else {
          isValid = true;
        }
        break;
      case 'template_language':
        isValid = true;
        break;
      default:
        break;
    }

    changeScreen(isValid, name);
    return { isValid, errorMessage };
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const newValues = { ...values, [name]: value };
    setValues(newValues);

    // Validar campo em tempo real
    const validation = validateField(name, value);
    const newValidate = {
      ...validate,
      [name]: validation
    };
    setValidate(newValidate);

    // Verificar se todos os campos estão preenchidos e válidos
    const requiredFields = ['accountcode', 'type', 'url', 'token'];
    
    // Verificar se todos os campos obrigatórios estão preenchidos
    const allFieldsFilled = requiredFields.every(field => 
      newValues[field] && newValues[field].trim() !== ''
    );
    
    // Verificar se todos os campos são válidos
    const allFieldsValid = requiredFields.every(field => {
      if (field === name) {
        return validation.isValid;
      }
      // Se o campo já foi validado, usar o resultado; caso contrário, validar agora
      if (newValidate[field]) {
        return newValidate[field].isValid;
      }
      // Se o campo não foi validado ainda, validar agora se estiver preenchido
      if (newValues[field] && newValues[field].trim() !== '') {
        const fieldValidation = validateField(field, newValues[field]);
        return fieldValidation.isValid;
      }
      return false;
    });

    let messageValid = true;
    if (name === 'message') {
      messageValid = validation.isValid;
    } else if (newValues.message && newValues.message.trim() !== '') {
      const messageValidation = newValidate.message || validateField('message', newValues.message);
      messageValid = messageValidation.isValid;
    }

    let templateValid = true;
    if (name === 'template_name') {
      templateValid = validation.isValid;
    } else if (newValues.template_name && newValues.template_name.trim() !== '') {
      const templateValidation = newValidate.template_name || validateField('template_name', newValues.template_name);
      templateValid = templateValidation.isValid;
    }

    setDisable(!(allFieldsFilled && allFieldsValid && messageValid && templateValid));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (disable || saving) return;

    setSaving(true);

    try {
      // Validar todos os campos novamente
      const validations = {};
      let hasErrors = false;

      Object.keys(values).forEach(key => {
        const validation = validateField(key, values[key]);
        validations[key] = validation;
        if (!validation.isValid) hasErrors = true;
      });

      if (hasErrors) {
        setValidate(validations);
        setSaving(false);
        return;
      }

      const response = await updateIntegration(itemsSelectedToEdit.id_integration, values);

      if (response && response.success) {
        toggleIsChangeFormElements();
        toggleIsHidden();
        history.push('/config/integracoes/lista');
      } else {
        console.error('Erro ao atualizar integração:', response);
        // Mostrar mensagem de erro
      }
    } catch (error) {
      console.error('Erro ao atualizar integração:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="container is-fluid">
      <div className="columns">
        <div className="column is-half is-offset-one-quarter has-text-centered">
          <strong className="is-size-3">EDITAR INTEGRAÇÃO</strong>
          <hr className="dropdown-divider"/>
        </div>
      </div>
      <div className="columns is-centered">
        <div className="column is-8">
          <div className="box">
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <h1 className="title is-4">
                    <FontAwesomeIcon icon={faPen} className="mr-2" />
                    Editar Integração #{itemsSelectedToEdit?.id_integration}
                  </h1>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <Link to="/config/integracoes/lista" className="button is-light">
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Voltar
                  </Link>
                </div>
              </div>
            </div>

            <div className="notification is-info is-light">
              <p>
                <strong>Atenção:</strong> Alterações na URL ou token podem afetar 
                integrações ativas. Verifique se os novos dados estão corretos 
                antes de salvar.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="columns">
                <div className="column is-6">
                  <div className="field">
                    <label className="label">Feixe *</label>
                    <div className="control">
                      <input
                        className="input"
                        type="text"
                        name="accountcode"
                        value={values.accountcode}
                        onChange={handleInputChange}
                        placeholder="Ex: abc"
                        maxLength="3"
                        required
                      />
                    </div>
                    <p 
                      className="help is-danger is-hidden" 
                      id="accountcode-invalid-data"
                    >
                      {validate.accountcode?.errorMessage}
                    </p>
                    <p className="help">
                      Código da conta (máximo 3 caracteres)
                    </p>
                  </div>
                </div>

                <div className="column is-6">
                  <div className="field">
                    <label className="label">DDD *</label>
                    <div className="control">
                      <input
                        className="input"
                        type="text"
                        name="type"
                        value={values.type}
                        onChange={handleInputChange}
                        placeholder="Ex: 5511"
                        maxLength="4"
                        required
                      />
                    </div>
                    <p 
                      className="help is-danger is-hidden" 
                      id="type-invalid-data"
                    >
                      {validate.type?.errorMessage}
                    </p>
                    <p className="help">
                      DDD do telefone do cliente
                    </p>
                  </div>
                </div>
              </div>

              <div className="field">
                <label className="label">URL da API *</label>
                <div className="control">
                  <input
                    className="input"
                    type="url"
                    name="url"
                    value={values.url}
                    onChange={handleInputChange}
                    placeholder="https://api.exemplo.com/webhook"
                    required
                  />
                </div>
                <p 
                  className="help is-danger is-hidden" 
                  id="url-invalid-data"
                >
                  {validate.url?.errorMessage}
                </p>
                <p className="help">
                  URL completa da API
                </p>
              </div>

              <div className="field">
                <label className="label">Token de Autenticação *</label>
                <div className="control">
                  <textarea
                    className="textarea"
                    name="token"
                    value={values.token}
                    onChange={handleInputChange}
                    placeholder="Token de autenticação da API"
                    rows="3"
                    required
                  />
                </div>
                <p 
                  className="help is-danger is-hidden" 
                  id="token-invalid-data"
                >
                  {validate.token?.errorMessage}
                </p>
                <p className="help">
                  Token de autenticação para acessar a API
                </p>
              </div>

              <div className="field">
                <label className="label">Mensagem (Opcional)</label>
                <div className="control">
                  <textarea
                    className="textarea"
                    name="message"
                    value={values.message}
                    onChange={handleInputChange}
                    placeholder="Mensagem adicional sobre esta integração (máximo 280 caracteres)"
                    rows="3"
                    maxLength="280"
                  />
                </div>
                <p 
                  className="help is-danger is-hidden" 
                  id="message-invalid-data"
                >
                  {validate.message?.errorMessage}
                </p>
                <p className="help">
                  Mensagem opcional para descrever a integração ({values.message.length}/280 caracteres)
                </p>
              </div>

              <hr />

              <h3 className="title is-6">Configuração de Template (Opcional)</h3>
              <div className="columns">
                <div className="column is-6">
                  <div className="field">
                    <label className="label">Nome do Template</label>
                    <div className="control">
                      <input
                        className="input"
                        type="text"
                        name="template_name"
                        value={values.template_name}
                        onChange={handleInputChange}
                        placeholder="Ex: ivr_atendimento_whatsapp"
                        maxLength="100"
                      />
                    </div>
                    <p 
                      className="help is-danger is-hidden" 
                      id="template_name-invalid-data"
                    >
                      {validate.template_name?.errorMessage}
                    </p>
                    <p className="help">
                      Nome do template aprovado no WhatsApp Cloud (somente letras minúsculas, números e underline)
                    </p>
                  </div>
                </div>
                <div className="column is-6">
                  <div className="field">
                    <label className="label">Idioma do Template</label>
                    <div className="control">
                      <select
                        className="input"
                        name="template_language"
                        value={values.template_language}
                        onChange={handleInputChange}
                        disabled={!values.template_name || values.template_name.trim() === ''}
                      >
                        <option value="pt_BR">pt_BR</option>
                        <option value="en_US">en_US</option>
                        <option value="en">en</option>
                        <option value="es">es</option>
                        <option value="es_AR">es_AR</option>
                        <option value="es_MX">es_MX</option>
                      </select>
                    </div>
                    <p className="help">
                      Idioma configurado no template do WhatsApp Cloud
                    </p>
                  </div>
                </div>
              </div>

              <hr />

              <div className="field is-grouped">
                <div className="control">
                  <button
                    type="submit"
                    className={`button is-primary ${saving ? 'is-loading' : ''}`}
                    disabled={disable || saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
                <div className="control">
                  <Link to="/config/integracoes/lista" className="button is-light">
                    Cancelar
                  </Link>
                </div>
              </div>
            </form>
          </div>

          {/* Informações da Integração */}
          <div className="box">
            <h2 className="title is-5">ℹ️ Informações</h2>
            <div className="content">
              <div className="columns">
                <div className="column">
                  <p><strong>ID:</strong> #{itemsSelectedToEdit?.id_integration}</p>
                  <p><strong>Criado em:</strong> {itemsSelectedToEdit?.created_at ? new Date(itemsSelectedToEdit.created_at).toLocaleString('pt-BR') : 'N/A'}</p>
                </div>
                <div className="column">
                  <p><strong>Última atualização:</strong> {itemsSelectedToEdit?.updated_at ? new Date(itemsSelectedToEdit.updated_at).toLocaleString('pt-BR') : 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IntegrationsEdit;
